import { Input } from '@/components/form/Input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import {
  HTTP_METHODS_LIST,
  type HTTPMethod,
} from '@/sections/asset/components/tabs/credentials/RecipeWizard/recipePayload';
import type { StepProps } from '@/sections/asset/components/tabs/credentials/RecipeWizard/RecipeWizard.types';
import {
  CONTENT_TYPE_PRESETS,
  CT_NONE,
  defaultBody,
} from '@/sections/asset/components/tabs/credentials/RecipeWizard/wizardConfig';

import { KVRowsEditor } from './KVRowsEditor';
import { Field, SectionIntro } from './shared';

export function RequestStep({ data, setData }: StepProps) {
  const onContentTypeChange = (next: string) => {
    const value = next === CT_NONE ? '' : next;
    setData(prev => {
      // Auto-update the body template iff the user hasn't customized it yet —
      // i.e. the body still matches the default for the previous Content-Type
      // (or is empty). Once they edit, we never overwrite their work.
      const wasDefault =
        prev.body === '' || prev.body === defaultBody(prev.contentType);
      return {
        ...prev,
        contentType: value,
        body: wasDefault ? defaultBody(value) : prev.body,
      };
    });
  };

  const ctSelectValue = data.contentType === '' ? CT_NONE : data.contentType;

  return (
    <div className="space-y-5">
      <SectionIntro
        title="Where does your app receive the login?"
        body={
          <p>
            Prefilled with the most common shape — adjust if your app does
            something different.
          </p>
        }
      />

      <div className="flex items-end gap-2">
        <Field label="Method" className="w-32">
          <Select
            value={data.method}
            onValueChange={v =>
              setData(prev => ({ ...prev, method: v as HTTPMethod }))
            }
          >
            <SelectTrigger data-testid="wizard-request-method">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {HTTP_METHODS_LIST.map(m => (
                <SelectItem key={m} value={m}>
                  {m}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </Field>
        <div className="flex-1">
          <Input
            label="Login URL"
            name="wizard-request-url"
            value={data.url}
            onChange={e => setData(prev => ({ ...prev, url: e.target.value }))}
            placeholder="https://your-app.com/login"
            required
            data-testid="wizard-request-url"
          />
        </div>
      </div>

      <Field
        label="Body format (Content-Type)"
        hint={
          <>
            Most login forms use <code>application/x-www-form-urlencoded</code>.
            Modern API logins typically use <code>application/json</code>.
          </>
        }
      >
        <Select value={ctSelectValue} onValueChange={onContentTypeChange}>
          <SelectTrigger
            className="w-full sm:w-80"
            data-testid="wizard-request-content-type"
          >
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={CT_NONE}>Not set</SelectItem>
            {CONTENT_TYPE_PRESETS.map(ct => (
              <SelectItem key={ct} value={ct}>
                {ct}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </Field>

      <Field
        label="Request body"
        hint={
          <>
            Use <code>{`{{username}}`}</code>, <code>{`{{password}}`}</code>,
            etc. anywhere you want one of your credentials substituted in.
          </>
        }
      >
        <Textarea
          name="wizard-request-body"
          rows={4}
          value={data.body}
          onChange={e => setData(prev => ({ ...prev, body: e.target.value }))}
          placeholder={defaultBody(data.contentType)}
          data-testid="wizard-request-body"
          className="font-mono text-sm"
        />
      </Field>

      <Field
        label="Additional headers (optional)"
        hint={
          <>
            Add headers your app expects on login — e.g.{' '}
            <code>X-CSRF-Token</code>, <code>Origin</code>,{' '}
            <code>X-Requested-With</code>. Content-Type is set above and
            doesn&apos;t need to be repeated.
          </>
        }
      >
        <KVRowsEditor
          rows={data.extraHeaders}
          onChange={extraHeaders =>
            setData(prev => ({ ...prev, extraHeaders }))
          }
          keyHeader="Header"
          keyPlaceholder="X-CSRF-Token"
          addLabel="Add header"
          testIdPrefix="wizard-header"
        />
      </Field>
    </div>
  );
}
